import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { Modal, message } from 'antd';
import { Link } from 'react-router-dom';
import {
  ToolOutlined,
  ClockCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { getLoggedInUser } from 'utilities/helpers';
import { handleSessionTermination } from 'utilities/apiClient';
import { status as getMaintenanceStatus } from 'features/maintenance/redux/api';
import {
  consumeFreshLogin,
  isModalAcked,
  ackModal
} from 'features/maintenance/maintenanceStorage';
import {
  formatCountdown,
  formatClockTime
} from 'features/maintenance/maintenanceFormat';
import styles from './index.module.less';

const POLL_MS = 30 * 1000;
const FAST_POLL_MS = 10 * 1000;
const FAST_POLL_BELOW_SECONDS = 2 * 60;
const WARNING_BELOW_SECONDS = 30 * 60;
const DANGER_BELOW_SECONDS = 10 * 60;
// Pop-up warnings, most distant first. Each shows once per window per tab.
const MODAL_THRESHOLDS_SECONDS = [30 * 60, 10 * 60, 2 * 60];

// Kept at module level (not component state) so the banner does not blink out
// and re-poll every time the Layout remounts on a route change.
let lastSnapshot = null; // { status, receivedAt }
let lastFetchAt = 0;
let inflight = null;
let openModal = null;

// Makes the banner re-check on its next tick (within a second) instead of
// waiting for the poll interval. Called by the admin page after a change.
export const requestMaintenanceStatusRefresh = () => {
  lastFetchAt = 0;
};

const closeModal = () => {
  if (openModal) {
    openModal.destroy();
    openModal = null;
  }
};

const showModal = (kind, props) => {
  closeModal();
  openModal = Modal[kind]({
    okText: 'I understand',
    centered: true,
    ...props,
    onOk: () => {
      openModal = null;
    }
  });
};

// Seconds left, counted from the server's own figure — never from the PC clock,
// which is often wrong on shop-floor machines.
const remainingSeconds = (snapshot, now) =>
  snapshot?.status?.active
    ? Math.max(
        0,
        snapshot.status.seconds_remaining - (now - snapshot.receivedAt) / 1000
      )
    : 0;

const minutesText = (seconds) => {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
};

const reasonLine = (status) =>
  status?.reason ? <p className="mb-0 mt-2">Reason: {status.reason}</p> : null;

const showThresholdModal = (threshold, remaining, status) => {
  const time = formatClockTime(status.logout_at);
  if (threshold > DANGER_BELOW_SECONDS) {
    showModal('warning', {
      title: `Scheduled maintenance in ${minutesText(remaining)}`,
      content: (
        <>
          <p className="mb-0">
            You will be logged out at <b>{time}</b> for scheduled maintenance.
            Complete and save your current activity. Do not start a new
            dispensing or eBMR step.
          </p>
          {reasonLine(status)}
        </>
      )
    });
  } else {
    showModal('error', {
      title: `Logging out in ${minutesText(remaining)}`,
      content: (
        <>
          <p className="mb-0">
            Save your work now. Anything not saved by <b>{time}</b> will be lost
            when you are logged out for maintenance.
          </p>
          {reasonLine(status)}
        </>
      )
    });
  }
};

const showFreshLoginModal = (remaining, status) => {
  const time = formatClockTime(status.logout_at);
  showModal('info', {
    title: `Scheduled maintenance at ${time}`,
    content: (
      <>
        <p className="mb-0">
          Nucleus will go down for maintenance in about {minutesText(remaining)}
          . You can continue working, but you will be logged out at{' '}
          <b>{time}</b>. Plan your work so that it is saved before then.
        </p>
        {reasonLine(status)}
      </>
    )
  });
};

// Toasts for changes made by the admin while this user was working.
const notifyTransition = (prev, next) => {
  if (prev?.active && !prev.cutoff_passed && !next.active) {
    closeModal();
    message.info({
      content: 'Scheduled maintenance has been cancelled.',
      key: 'maintenance',
      duration: 6
    });
  } else if (
    prev?.active &&
    next.active &&
    prev.id === next.id &&
    new Date(next.logout_at) > new Date(prev.logout_at)
  ) {
    closeModal();
    message.info({
      content: `Maintenance rescheduled. You will now be logged out at ${formatClockTime(
        next.logout_at
      )}.`,
      key: 'maintenance',
      duration: 6
    });
  }
};

const MaintenanceBanner = () => {
  const { loggedIn, user } = getLoggedInUser();
  const isAdmin = user?.role === 'admin';

  const [snapshot, setSnapshot] = useState(lastSnapshot);
  const [now, setNow] = useState(Date.now());
  const [spacerHeight, setSpacerHeight] = useState(0);
  const bannerRef = useRef(null);
  const mountedRef = useRef(true);
  const logoutCheckRef = useRef(false);

  // Resolves to the latest status, or null when the server could not be reached.
  const fetchStatus = useCallback(() => {
    if (inflight) return inflight;
    lastFetchAt = Date.now();
    inflight = getMaintenanceStatus()
      .then((res) => {
        if (!res?.success || !res?.data) return null;
        const prev = lastSnapshot?.status;
        lastSnapshot = { status: res.data, receivedAt: Date.now() };
        if (mountedRef.current) setSnapshot(lastSnapshot);
        if (!isAdmin) notifyTransition(prev, res.data);
        return res.data;
      })
      .catch(() => null)
      .finally(() => {
        inflight = null;
      });
    return inflight;
  }, [isAdmin]);

  useEffect(() => {
    mountedRef.current = true;
    if (!loggedIn) return undefined;

    if (!lastSnapshot || Date.now() - lastFetchAt >= FAST_POLL_MS) {
      fetchStatus();
    }
    const tick = setInterval(() => setNow(Date.now()), 1000);
    // Browsers slow timers in background tabs — re-check as soon as the tab is back.
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchStatus();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      mountedRef.current = false;
      clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loggedIn, fetchStatus]);

  const status = snapshot?.status;
  const remaining = remainingSeconds(snapshot, now);

  // Polling, pop-ups and the logout itself, evaluated every second.
  useEffect(() => {
    if (!loggedIn) return;

    const pollEvery =
      status?.active && remaining < FAST_POLL_BELOW_SECONDS
        ? FAST_POLL_MS
        : POLL_MS;
    if (Date.now() - lastFetchAt >= pollEvery) fetchStatus();

    if (!status?.active || isAdmin) return;

    if (status.cutoff_passed || remaining <= 0) {
      if (logoutCheckRef.current) return;
      logoutCheckRef.current = true;
      // Confirm with the server first — the admin may have cancelled or extended
      // in the last seconds. If the server is unreachable (deployment already
      // under way), log out anyway.
      fetchStatus().then((latest) => {
        if (
          latest &&
          (!latest.active ||
            (!latest.cutoff_passed && latest.seconds_remaining > 0))
        ) {
          logoutCheckRef.current = false;
          return;
        }
        closeModal();
        handleSessionTermination({ code: 'MAINTENANCE' });
      });
      return;
    }

    const crossed = MODAL_THRESHOLDS_SECONDS.filter((t) => remaining <= t);
    if (crossed.length) {
      // Keyed by logout_at too, so an extension re-arms the warnings.
      const keyBase = `${status.id}_${status.logout_at}`;
      const urgent = crossed[crossed.length - 1];
      if (!isModalAcked(`${keyBase}_${urgent}`)) {
        crossed.forEach((t) => ackModal(`${keyBase}_${t}`));
        showThresholdModal(urgent, remaining, status);
      }
    }
  }, [now, status, remaining, isAdmin, loggedIn, fetchStatus]);

  // One-time pop-up for a user who signs in while a countdown is running.
  useEffect(() => {
    if (!loggedIn || !status || isAdmin) return;
    if (!consumeFreshLogin()) return;
    if (
      status.active &&
      !status.cutoff_passed &&
      remainingSeconds(snapshot, Date.now()) > MODAL_THRESHOLDS_SECONDS[0]
    ) {
      showFreshLoginModal(remainingSeconds(snapshot, Date.now()), status);
    }
  }, [snapshot]); // eslint-disable-line react-hooks/exhaustive-deps

  // The banner is position: fixed under the fixed header; the spacer keeps it
  // from covering the top of the page content.
  useLayoutEffect(() => {
    const height = bannerRef.current ? bannerRef.current.offsetHeight : 0;
    if (height !== spacerHeight) setSpacerHeight(height);
  });

  if (!loggedIn || !status?.active) return null;

  const time = formatClockTime(status.logout_at);
  const countdown = (
    <span className={styles.countdown}>{formatCountdown(remaining)}</span>
  );
  const cutoffReached = status.cutoff_passed || remaining <= 0;

  let tone;
  let icon;
  let text;
  if (isAdmin) {
    tone = styles.admin;
    icon = <ToolOutlined />;
    text = cutoffReached ? (
      <>
        <b>Maintenance mode is ON.</b> Users are logged out and sign-in is
        blocked for everyone except admins. End maintenance once the deployment
        is complete.
      </>
    ) : (
      <>
        <b>Maintenance mode is ON.</b> All non-admin users will be logged out at{' '}
        <b>{time}</b> (in <b>{countdown}</b>).
      </>
    );
  } else if (cutoffReached) {
    tone = styles.danger;
    icon = <WarningOutlined />;
    text = <b>Logging out for scheduled maintenance…</b>;
  } else if (remaining <= DANGER_BELOW_SECONDS) {
    tone = styles.danger;
    icon = <WarningOutlined />;
    text = (
      <>
        <b>Logging out in {countdown}</b> for scheduled maintenance. Save your
        work now.
      </>
    );
  } else if (remaining <= WARNING_BELOW_SECONDS) {
    tone = styles.warning;
    icon = <ClockCircleOutlined />;
    text = (
      <>
        <b>Logging out in {countdown}</b> for scheduled maintenance at{' '}
        <b>{time}</b>. Complete and save your current activity. Do not start a
        new dispensing or eBMR step.
      </>
    );
  } else {
    tone = styles.info;
    icon = <ClockCircleOutlined />;
    text = (
      <>
        Scheduled maintenance at <b>{time}</b>. You will be logged out in{' '}
        <b>{countdown}</b>.
      </>
    );
  }

  return (
    <>
      <div ref={bannerRef} className={`${styles.banner} ${tone}`} role="status">
        <span className={styles.icon}>{icon}</span>
        <span className={styles.text} title={status.reason || undefined}>
          {text}
          {status.reason ? (
            <span className={styles.reason}>Reason: {status.reason}</span>
          ) : null}
        </span>
        {isAdmin ? (
          <Link className={styles.action} to="/maintenance-mode">
            Manage
          </Link>
        ) : null}
      </div>
      <div style={{ height: spacerHeight }} />
    </>
  );
};

export default MaintenanceBanner;
